import { useCallback, useMemo } from 'react';
import { createStore, useStore, type Store } from '../lib/store';
import { listStorageKeys, onStorageChange, readStringList, scopedKey, writeStringList } from './localDb';

// Reading list (reading_list_provider.dart + reading_list_book_codec.dart).
// Stored per library scope (`libraryScopeKey(libraryId)`) under
// `reading_list_v1:{scope}` as a JSON array of JSON-encoded book snapshots,
// newest additions first, in the user's order.

export const READING_LIST_KEY_PREFIX = 'reading_list_v1';

export interface ReadingListChapter {
  id: string;
  start: number;
  end: number;
  title: string;
}

/** Book snapshot in Flutter's codec shape (values as stored when added or last refreshed). */
export interface ReadingListBook {
  id: string;
  title: string;
  author: string;
  authorId: string | null;
  seriesId: string | null;
  seriesName: string | null;
  /** Seconds. */
  duration: number;
  coverUrl: string;
  audioUrl: string;
  chapters: ReadingListChapter[];
  /** Seconds listened. */
  userProgress: number;
  isFinished: boolean;
  /** Epoch seconds. */
  finishedAt: number | null;
  /** Epoch ms. */
  addedAt: number;
  updatedAt: number | null;
  sizeInBytes: number | null;
  fileBitrateKbps: number | null;
  fileCodec: string | null;
  fileContainer: string | null;
  description: string;
  publisher: string;
  publishedYear: number | null;
  genres: string[];
  narrators: string[];
  isDownloaded: boolean;
}

export function readingListKey(scope: string): string {
  return scopedKey(READING_LIST_KEY_PREFIX, scope);
}

// ---------------------------------------------------------------- codec

const text = (v: unknown): string | undefined => (v == null ? undefined : String(v));
function nullableString(v: unknown): string | null {
  const t = text(v)?.trim() ?? '';
  return t ? t : null;
}
function nullableInt(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : null;
  if (typeof v === 'string' && /^\s*[+-]?\d+\s*$/.test(v)) return Number.parseInt(v, 10);
  return null;
}
function toDouble(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => (x == null ? '' : String(x).trim())).filter(Boolean) : [];
}
function chapters(v: unknown): ReadingListChapter[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object' && !Array.isArray(c))
    .map((c) => ({ id: text(c.id) ?? '', start: toDouble(c.start), end: toDouble(c.end), title: text(c.title) ?? 'Untitled Chapter' }));
}

/** Media URLs carry the session token as `?token=`; never persist it in a snapshot. */
export function stripTokenParams(url: string): string {
  if (!url || !/[?&](token|access_token)=/i.test(url)) return url;
  try {
    const u = new URL(url, 'http://plume.invalid');
    for (const key of [...u.searchParams.keys()]) if (/token|auth|secret|api_key/i.test(key)) u.searchParams.delete(key);
    return url.startsWith('http') ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}

/** Builds a snapshot from any object with Flutter's field names, applying the codec's defaults. */
export function toReadingListBook(raw: Record<string, unknown>): ReadingListBook {
  return {
    id: text(raw.id) ?? '',
    title: text(raw.title) ?? 'Untitled',
    author: text(raw.author) ?? 'Unknown Author',
    authorId: nullableString(raw.authorId),
    seriesId: nullableString(raw.seriesId),
    seriesName: nullableString(raw.seriesName),
    duration: toDouble(raw.duration),
    coverUrl: stripTokenParams(text(raw.coverUrl) ?? ''),
    audioUrl: stripTokenParams(text(raw.audioUrl) ?? ''),
    chapters: chapters(raw.chapters),
    userProgress: toDouble(raw.userProgress),
    isFinished: raw.isFinished === true,
    finishedAt: nullableInt(raw.finishedAt),
    addedAt: nullableInt(raw.addedAt) ?? 0,
    updatedAt: nullableInt(raw.updatedAt),
    sizeInBytes: nullableInt(raw.sizeInBytes),
    fileBitrateKbps: nullableInt(raw.fileBitrateKbps),
    fileCodec: nullableString(raw.fileCodec),
    fileContainer: nullableString(raw.fileContainer),
    description: text(raw.description) ?? '',
    publisher: text(raw.publisher) ?? '',
    publishedYear: nullableInt(raw.publishedYear),
    genres: stringList(raw.genres),
    narrators: stringList(raw.narrators),
    isDownloaded: raw.isDownloaded === true,
  };
}

export function encodeReadingListBook(book: ReadingListBook): string {
  return JSON.stringify({
    id: book.id,
    title: book.title,
    author: book.author,
    authorId: book.authorId,
    seriesId: book.seriesId,
    seriesName: book.seriesName,
    duration: book.duration,
    coverUrl: stripTokenParams(book.coverUrl),
    audioUrl: stripTokenParams(book.audioUrl),
    chapters: book.chapters.map((c) => ({ id: c.id, start: c.start, end: c.end, title: c.title })),
    userProgress: book.userProgress,
    isFinished: book.isFinished,
    finishedAt: book.finishedAt,
    addedAt: book.addedAt,
    updatedAt: book.updatedAt,
    sizeInBytes: book.sizeInBytes,
    fileBitrateKbps: book.fileBitrateKbps,
    fileCodec: book.fileCodec,
    fileContainer: book.fileContainer,
    description: book.description,
    publisher: book.publisher,
    publishedYear: book.publishedYear,
    genres: book.genres,
    narrators: book.narrators,
    isDownloaded: book.isDownloaded,
  });
}

/** Null for anything that is not a JSON object (the entry is dropped). */
export function decodeReadingListBook(raw: string): ReadingListBook | null {
  try {
    const decoded: unknown = JSON.parse(raw);
    if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) return null;
    return toReadingListBook(decoded as Record<string, unknown>);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- pure list operations

/** Adds at the top; an existing entry moves to the top with the new snapshot. */
export function addToList(books: readonly ReadingListBook[], book: ReadingListBook): ReadingListBook[] {
  return [book, ...books.filter((b) => b.id !== book.id)];
}

export function removeFromList(books: readonly ReadingListBook[], bookId: string): ReadingListBook[] | null {
  const index = books.findIndex((b) => b.id === bookId);
  if (index < 0) return null;
  return [...books.slice(0, index), ...books.slice(index + 1)];
}

/**
 * Flutter `ReorderableListView.onReorder` semantics: `newIndex` is the slot
 * before removal (moving down passes index + 1). Null when nothing moves.
 */
export function reorderList(books: readonly ReadingListBook[], oldIndex: number, newIndex: number): ReadingListBook[] | null {
  if (oldIndex < 0 || oldIndex >= books.length) return null;
  const target = oldIndex < newIndex ? newIndex - 1 : newIndex;
  return moveInList(books, oldIndex, target);
}

/** Moves to a final index (for drag-and-drop code that reports the destination). Null when nothing moves. */
export function moveInList(books: readonly ReadingListBook[], fromIndex: number, toIndex: number): ReadingListBook[] | null {
  if (fromIndex < 0 || fromIndex >= books.length || toIndex < 0 || toIndex >= books.length || fromIndex === toIndex) return null;
  const next = [...books];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved!);
  return next;
}

/**
 * Puts `book` back at `index` (clamped to the list), e.g. to undo a removal.
 * Null when the book is listed already (re-added meanwhile: that entry wins).
 */
export function insertInList(books: readonly ReadingListBook[], book: ReadingListBook, index: number): ReadingListBook[] | null {
  if (books.some((b) => b.id === book.id)) return null;
  const at = Math.min(Math.max(Number.isFinite(index) ? Math.trunc(index) : 0, 0), books.length);
  return [...books.slice(0, at), book, ...books.slice(at)];
}

export function replaceInList(books: readonly ReadingListBook[], book: ReadingListBook): ReadingListBook[] | null {
  const index = books.findIndex((b) => b.id === book.id);
  if (index < 0) return null;
  const next = [...books];
  next[index] = book;
  return next;
}

// ---------------------------------------------------------------- persistence + stores

export interface ReadingListState {
  books: ReadingListBook[];
  /** Always false: storage reads are synchronous (kept for Flutter's loading state contract). */
  isLoading: boolean;
}

export function loadReadingList(scope: string): ReadingListBook[] {
  return readStringList(readingListKey(scope))
    .map(decodeReadingListBook)
    .filter((b): b is ReadingListBook => b !== null);
}

function persist(scope: string, books: readonly ReadingListBook[]) {
  const entries = books.map(encodeReadingListBook).map((e) => e.trim()).filter(Boolean);
  writeStringList(readingListKey(scope), entries);
}

const stores = new Map<string, Store<ReadingListState>>();
const EMPTY: ReadingListState = Object.freeze({ books: [], isLoading: false }) as ReadingListState;
const emptyStore = createStore<ReadingListState>(EMPTY);

export function readingListStore(scope: string | null): Store<ReadingListState> {
  const key = scope?.trim();
  if (!key) return emptyStore;
  let store = stores.get(key);
  if (!store) {
    store = createStore<ReadingListState>({ books: loadReadingList(key), isLoading: false });
    stores.set(key, store);
  }
  return store;
}

onStorageChange(
  (key) => key.startsWith(`${READING_LIST_KEY_PREFIX}:`),
  (key) => {
    for (const [scope, store] of stores) {
      if (key === null || key === readingListKey(scope)) store.set({ books: loadReadingList(scope), isLoading: false });
    }
  },
);

/** Re-reads every loaded scope from storage (after a backup import). */
export function reloadReadingLists(): void {
  for (const [scope, store] of stores) store.set({ books: loadReadingList(scope), isLoading: false });
}

function commit(scope: string | null, update: (books: readonly ReadingListBook[]) => ReadingListBook[] | null): boolean {
  const key = scope?.trim();
  if (!key) return false;
  const store = readingListStore(key);
  const next = update(store.get().books);
  if (!next) return false;
  store.set({ books: next, isLoading: false });
  persist(key, next);
  return true;
}

export function getReadingList(scope: string | null): ReadingListBook[] {
  return readingListStore(scope).get().books;
}

export function isInReadingList(scope: string | null, bookId: string): boolean {
  return getReadingList(scope).some((b) => b.id === bookId);
}

export function addToReadingList(scope: string | null, book: ReadingListBook): boolean {
  return commit(scope, (books) => addToList(books, book));
}

export function removeFromReadingList(scope: string | null, bookId: string): boolean {
  return commit(scope, (books) => removeFromList(books, bookId));
}

/** Inserts the stored snapshot at `index` (Undo after a removal); false when it is listed already. */
export function insertIntoReadingList(scope: string | null, book: ReadingListBook, index: number): boolean {
  return commit(scope, (books) => insertInList(books, book, index));
}

/**
 * Removes `bookId` and returns the Undo: it puts the stored snapshot back at
 * the index it had (and returns whether it did; nothing happens once the book
 * is listed again). Null when the book was not listed.
 */
export function removeFromReadingListUndoable(scope: string | null, bookId: string): (() => boolean) | null {
  const books = getReadingList(scope);
  const index = books.findIndex((b) => b.id === bookId);
  const entry = books[index];
  if (!entry || !removeFromReadingList(scope, bookId)) return null;
  return () => insertIntoReadingList(scope, entry, index);
}

/** Adds or removes; returns what happened (for the "Added/Removed … reading list." toast). */
export function toggleReadingList(scope: string | null, book: ReadingListBook): 'added' | 'removed' | null {
  if (!scope?.trim()) return null;
  if (isInReadingList(scope, book.id)) return removeFromReadingList(scope, book.id) ? 'removed' : null;
  return addToReadingList(scope, book) ? 'added' : null;
}

/** Flutter `onReorder(oldIndex, newIndex)` semantics. */
export function reorderReadingList(scope: string | null, oldIndex: number, newIndex: number): boolean {
  return commit(scope, (books) => reorderList(books, oldIndex, newIndex));
}

/** Final-index move. */
export function moveReadingListItem(scope: string | null, fromIndex: number, toIndex: number): boolean {
  return commit(scope, (books) => moveInList(books, fromIndex, toIndex));
}

/** Replaces the stored snapshot if the book is listed (after Reset Progress / Mark as Finished). */
export function refreshReadingListBook(scope: string | null, book: ReadingListBook): boolean {
  return commit(scope, (books) => replaceInList(books, book));
}

/**
 * Applies `update` to `bookId`'s snapshot in every reading list of one account
 * (`accountScope` = `userScopeKey()`; lists are kept per library). Used by the
 * progress actions so a book shows its new state whichever library listed it.
 * Returns how many lists changed.
 */
export function updateReadingListSnapshots(accountScope: string | null, bookId: string, update: (book: ReadingListBook) => ReadingListBook): number {
  const account = accountScope?.trim();
  if (!account) return 0;
  const libraryPrefix = `${account}|`;
  const scopes = new Set(listStorageKeys(`${READING_LIST_KEY_PREFIX}:${libraryPrefix}`).map((key) => key.slice(READING_LIST_KEY_PREFIX.length + 1)));
  for (const scope of stores.keys()) if (scope.startsWith(libraryPrefix)) scopes.add(scope);
  let changed = 0;
  for (const scope of scopes) {
    const current = getReadingList(scope).find((b) => b.id === bookId);
    if (current && refreshReadingListBook(scope, update(current))) changed++;
  }
  return changed;
}

/** Toast copy for a toggle result. */
export function readingListToastMessage(result: 'added' | 'removed', title: string): string {
  return result === 'added' ? `Added "${title}" to your reading list.` : `Removed "${title}" from your reading list.`;
}

// ---------------------------------------------------------------- hooks

export interface ReadingListView extends ReadingListState {
  count: number;
  contains: (bookId: string) => boolean;
  add: (book: ReadingListBook) => boolean;
  remove: (bookId: string) => boolean;
  toggle: (book: ReadingListBook) => 'added' | 'removed' | null;
  reorder: (oldIndex: number, newIndex: number) => boolean;
  move: (fromIndex: number, toIndex: number) => boolean;
  refreshBook: (book: ReadingListBook) => boolean;
}

export function useReadingList(scope: string | null): ReadingListView {
  const state = useStore(readingListStore(scope));
  return useMemo(
    () => ({
      ...state,
      count: state.books.length,
      contains: (id: string) => state.books.some((b) => b.id === id),
      add: (book: ReadingListBook) => addToReadingList(scope, book),
      remove: (id: string) => removeFromReadingList(scope, id),
      toggle: (book: ReadingListBook) => toggleReadingList(scope, book),
      reorder: (from: number, to: number) => reorderReadingList(scope, from, to),
      move: (from: number, to: number) => moveReadingListItem(scope, from, to),
      refreshBook: (book: ReadingListBook) => refreshReadingListBook(scope, book),
    }),
    [state, scope],
  );
}

export function useIsInReadingList(scope: string | null, bookId: string): boolean {
  const select = useCallback((s: ReadingListState) => s.books.some((b) => b.id === bookId), [bookId]);
  return useStore(readingListStore(scope), select);
}
